import { Component, OnInit, AfterViewInit } from '@angular/core';
import { AuthService } from 'src/core/services/auth.service';
import { CognitoUserInterface } from '@aws-amplify/ui-components';
import {
  ChartConfig,
  DashboardPayload,
  DeliveryPayload,
  ChartValue,
  DashboardData,
  MultiChartValue,
  criterias,
  Criteria,
  medicineColorsDomain,
  MedicineReportPayload,
} from 'src/interfaces/dashboard';
import { ScaleType } from '@swimlane/ngx-charts';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from 'src/core/services/dashboard.service';
import { saveAs } from 'file-saver';
import { PrescriptionService } from 'src/core/services/prescription.service';
import { Order, TrackingDetails } from 'src/interfaces/order';
import { Batch } from 'src/interfaces/batch';
import { MedicineService } from 'src/core/services/medicine.service';
import { Individual } from 'src/interfaces/individual';
import { Company } from 'src/interfaces/company';
import { LoaderService } from 'src/core/services/loader.service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { IndividualService } from 'src/core/services/individual.service';
import { CompanyService } from 'src/core/services/company.service';
import { Workbook, Worksheet } from 'exceljs';
import { UtilsService } from 'src/core/services/utils.service';
import { BatchService } from 'src/core/services/batch.service';
import { AlertService } from 'src/core/services/alert.service';

interface StartAndEndDatePayload {
  start_date: string;
  end_date: string;
}

@Component({
  selector: 'core-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, AfterViewInit {
  currentUser: CognitoUserInterface | null = null;
  batchesConfig: ChartConfig;
  ordersConfig: ChartConfig;
  medicineReportConfig: ChartConfig;
  ordersData: ChartValue[] = [];
  batchesData: ChartValue[] = [];
  medicineReportData: MultiChartValue[] = [];
  medicineCriteriaName: string = 'Month';
  doctors: Individual[] = [];
  selectedDoctor: Individual | null = null;
  companies: Company[] = [];
  selectedCompany: Company | null = null;
  seperator: string = ';';
  selectedDistributor: Company | null = null;
  distributors: Company[] = [];
  selectedDeliveryStartDate: Date | null = null;
  selectedDeliveryEndDate: Date | null = null;

  selectedMedicineStartDate: Date | null = null;
  selectedMedicineEndDate: Date | null = null;
  selectedCriteria: string = 'monthly';
  trackingDetails: TrackingDetails[] = [];

  ordersDateRangeForm = this._formBuilder.group({
    start: ['', Validators.required],
    end: ['', Validators.required],
    criteria: ['monthly'],
  });

  batchesDateRangeForm = this._formBuilder.group({
    start: ['', Validators.required],
    end: ['', Validators.required],
    criteria: ['monthly'],
  });

  medicineDateRangeForm = this._formBuilder.group({
    start: ['', Validators.required],
    end: ['', Validators.required],
    criteria: ['monthly'],
    doctorName: [''],
    companyName: [''],
  });

  deliveryReportDataForm = this._formBuilder.group({
    start: ['', Validators.required],
    end: ['', Validators.required],
    criteria: ['monthly'],
    distributorName: [''],
  });
  downloadOrdersReportControl = this._formBuilder.control(
    '',
    Validators.required
  );

  downloadBatchesReportControl = this._formBuilder.control(
    '',
    Validators.required
  );

  downloadMedicineReportControl = this._formBuilder.control(
    '',
    Validators.required
  );

  dashboardData: DashboardData | null = null;

  criterias = criterias;

  isLoading: boolean = false;

  constructor(
    private _auth: AuthService,
    private _formBuilder: FormBuilder,
    private _route: ActivatedRoute,
    private _dashboardService: DashboardService,
    private _prescriptionService: PrescriptionService,
    private _batchService: BatchService,
    private _medicineService: MedicineService,
    private _loaderService: LoaderService,
    private _individualService: IndividualService,
    private _companyService: CompanyService,
    private _router: Router,
    private _utilService: UtilsService,
    private _alertService: AlertService
  ) {}
  diffDateRangeForm = this._formBuilder.group({
    start: ['', Validators.required],
    end: ['', Validators.required],
    criteria: ['monthly'],
  });
  

  downloadDelReportControl = this._formBuilder.control(
    '',
    Validators.required
  );

  /** Optional date for Vetscripts daily shipped report (YYYY-MM-DD); empty = today. */
  vetscriptsReportDateControl = this._formBuilder.control(null);
  vetscriptsReportLoading = false;

  // Split Orders report: download only (no load/table). POST report/split-orders.
  splitOrdersDateRangeForm = this._formBuilder.group({
    start: [null as Date | null],
    end: [null as Date | null],
  });
  selectedSplitDistributor: Company | null = null;
  splitOrdersDistributors: Company[] = [];
  splitOrdersDistributorName = this._formBuilder.control('');
  downloadSplitOrdersReportControl = this._formBuilder.control('', Validators.required);
  splitOrdersDownloadLoading = false;

  ngAfterViewInit(): void {}

  /** FormGroup.patchValue ignores disabled controls; use when loader may have disabled autocomplete fields. */
  private patchControlEvenIfDisabled(
    form: FormGroup,
    controlName: string,
    value: unknown
  ): void {
    const c = form.get(controlName);
    if (!c) return;
    const wasDisabled = c.disabled;
    if (wasDisabled) c.enable({ emitEvent: false });
    c.patchValue(value, { emitEvent: false });
    if (wasDisabled) c.disable({ emitEvent: false });
  }

  ngOnInit(): void {
    // Drive disabled state via FormControl.disable/enable — not [disabled] on inputs with formControlName (Angular warns).
    this._loaderService.isLoading.subscribe((loading) => {
      this.isLoading = loading;
      const doctor = this.medicineDateRangeForm.get('doctorName');
      const company = this.medicineDateRangeForm.get('companyName');
      const distributor = this.deliveryReportDataForm.get('distributorName');
      if (loading) {
        doctor?.disable({ emitEvent: false });
        company?.disable({ emitEvent: false });
        distributor?.disable({ emitEvent: false });
      } else {
        doctor?.enable({ emitEvent: false });
        company?.enable({ emitEvent: false });
        distributor?.enable({ emitEvent: false });
      }
    });

    this._route.data.subscribe((data) => {
      this.dashboardData = data.dashboardData.dashboardData;
      const dates: { start: string; end: string } = data.dashboardData.dates;

      this.ordersDateRangeForm.patchValue({
        start: new Date(dates.start),
        end: new Date(dates.end),
      });

      this.batchesDateRangeForm.patchValue({
        start: new Date(dates.start),
        end: new Date(dates.end),
      });

      this.medicineDateRangeForm.patchValue({
        start: new Date(dates.start),
        end: new Date(dates.end),
      });

      this.selectedMedicineStartDate = new Date(dates.start);
      this.selectedMedicineEndDate = new Date(dates.end);

      this.deliveryReportDataForm.patchValue({
        start: new Date(dates.start),
        end: new Date(dates.end),
      });
      
      this.selectedDeliveryStartDate = new Date(dates.start);
      this.selectedDeliveryEndDate = new Date(dates.end);

      if (this.dashboardData) {
        this.updateView(this.dashboardData);
      }
    });

    this.ordersDateRangeForm.valueChanges.subscribe((value) => {
      const startDate: Date | null = this.ordersDateRangeForm.value.start;

      const endDate: Date | null = this.ordersDateRangeForm.value.end;

      if (!endDate || !startDate) return;

      const startValue: string =
        startDate.getFullYear() +
        '-' +
        (startDate.getMonth() + 1) +
        '-' +
        startDate.getDate();

      const endValue: string =
        endDate.getFullYear() +
        '-' +
        (endDate.getMonth() + 1) +
        '-' +
        endDate.getDate();

      const payload: DashboardPayload = {
        start_date: startValue,
        end_date: endValue,
        type: value.criteria,
        doctor_id: undefined,
        company_id: undefined,
      };

      (async () => {
        this.ordersData = await this._dashboardService.getOrdersData(payload);
      })();
    });

    this.batchesDateRangeForm.valueChanges.subscribe((value) => {
      const startDate: Date | null = this.batchesDateRangeForm.value.start;

      const endDate: Date | null = this.batchesDateRangeForm.value.end;

      if (!endDate || !startDate) return;

      const startValue: string =
        startDate.getFullYear() +
        '-' +
        (startDate.getMonth() + 1) +
        '-' +
        startDate.getDate();

      const endValue: string =
        endDate.getFullYear() +
        '-' +
        (endDate.getMonth() + 1) +
        '-' +
        endDate.getDate();

      const payload: DashboardPayload = {
        start_date: startValue,
        end_date: endValue,
        type: value.criteria,
        doctor_id: undefined,
        company_id: undefined,
      };

      (async () => {
        this.batchesData = await this._dashboardService.getOrdersData(payload);
      })();
    });

    this.batchesConfig = {
      showXAxis: true,
      showYAxis: true,
      gradient: false,
      showLegend: true,
      showXAxisLabel: true,
      xAxisLabel: 'Status',
      showYAxisLabel: true,
      yAxisLabel: 'Number of batches',
      colorScheme: {
        name: 'batches',
        selectable: true,
        group: ScaleType.Ordinal,
        domain: ['#0033cc', '#3333cc', '#660033', '#333300'],
      },
    };

    this.ordersConfig = {
      showXAxis: true,
      showYAxis: true,
      gradient: false,
      showLegend: true,
      showXAxisLabel: true,
      xAxisLabel: 'Status',
      showYAxisLabel: true,
      yAxisLabel: 'Number of orders',

      colorScheme: {
        name: 'orders',
        selectable: true,
        group: ScaleType.Ordinal,
        domain: ['#336600', '#336699', '#333399', '#993399'],
      },
    };

    this.medicineReportConfig = {
      showXAxis: true,
      showYAxis: true,
      gradient: false,
      showLegend: true,
      showXAxisLabel: true,
      xAxisLabel: this.medicineCriteriaName,
      showYAxisLabel: true,
      yAxisLabel: 'Ordered',

      colorScheme: {
        name: 'orders',
        selectable: true,
        group: ScaleType.Ordinal,
        domain: medicineColorsDomain,
      },
    };

    this.handleMedicineFormChanges();
    this.handleDeliveryFormChanges();
    this.handleSplitOrdersFormChanges();
  }

  /** Build payload for split-orders report from current form values. */
  private buildSplitOrdersPayload(): { start_date?: string; end_date?: string; company_id?: number } {
    const payload: { start_date?: string; end_date?: string; company_id?: number } = {};
    const startVal = this.splitOrdersDateRangeForm.value.start as Date | null;
    const endVal = this.splitOrdersDateRangeForm.value.end as Date | null;
    if (startVal) {
      payload.start_date =
        startVal.getFullYear() + '-' + (startVal.getMonth() + 1) + '-' + startVal.getDate();
    }
    if (endVal) {
      payload.end_date =
        endVal.getFullYear() + '-' + (endVal.getMonth() + 1) + '-' + endVal.getDate();
    }
    if (this.selectedSplitDistributor?.id) payload.company_id = this.selectedSplitDistributor.id;
    return payload;
  }

  handleSplitOrdersFormChanges() {
    this.splitOrdersDistributorName.valueChanges.subscribe((value) => {
      if (value && typeof value === 'string') {
        (async () => {
          this.splitOrdersDistributors = await this._companyService.getCompanies(1, 10, value);
        })();
      } else {
        this.splitOrdersDistributors = [];
      }
    });
  }

  splitDistributorSelected(event: MatAutocompleteSelectedEvent) {
    this.selectedSplitDistributor = event.option.value;
    if (!this.selectedSplitDistributor) return;
    this.splitOrdersDistributorName.setValue(this.selectedSplitDistributor.name, { emitEvent: false });
  }

  deselectSplitDistributor() {
    this.selectedSplitDistributor = null;
    this.splitOrdersDistributorName.setValue('', { emitEvent: false });
  }

  async generateAndDownloadSplitOrders() {
    if (this.downloadSplitOrdersReportControl.value === 'csv') {
      await this.downloadSplitOrdersCSV();
    }
    if (this.downloadSplitOrdersReportControl.value === 'excel') {
      await this.downloadSplitOrdersExcel();
    }
  }

  async downloadSplitOrdersCSV() {
    const payload = this.buildSplitOrdersPayload();
    this.splitOrdersDownloadLoading = true;
    try {
      const data = await this._prescriptionService.reportSplitOrders(payload);
      const headers = [
        'Waybill No.', 'Split date', 'Order Id', 'Customer Order No.', 'Split Id',
        'Split Role', 'Date Order Placed', 'Customer', 'Distributor', 'Product', 'Pack Size',
        'Quantity ordered', 'Process', 'Price'
      ];
      const rows: string[] = [headers.join(this.seperator)];
      for (const row of data) {
        rows.push([
          row.waybill_number ?? '',
          row.split_date ?? '',
          row.order_id + '',
          row.customer_order_number ?? '',
          row.split_id != null ? row.split_id + '' : '',
          row.split_role ?? '',
          row.date_order_placed ?? '',
          row.customer ?? '',
          row.distributor ?? '',
          row.product ?? '',
          row.pack_size + '',
          row.quantity_ordered + '',
          row.process ?? '',
          row.price != null ? row.price + '' : '',
        ].join(this.seperator));
      }
      const blob = new Blob([rows.join('\r\n')], { type: 'text/csv' });
      saveAs(blob, 'SplitOrders.csv');
    } finally {
      this.splitOrdersDownloadLoading = false;
    }
  }

  async downloadSplitOrdersExcel() {
    const payload = this.buildSplitOrdersPayload();
    this.splitOrdersDownloadLoading = true;
    try {
      const data = await this._prescriptionService.reportSplitOrders(payload);
      const workBook = new Workbook();
      const sheet = workBook.addWorksheet('Split Orders');
      sheet.columns = [
        { header: 'Order Number', key: 'order_id', width: 12 },
        { header: 'Split Order Number', key: 'split_id', width: 12 },
        { header: 'Split Role', key: 'split_role', width: 10 },
        { header: 'Customer Order No.', key: 'customer_order_number', width: 20 },
        { header: 'Date Order Placed', key: 'date_order_placed', width: 20 },
        { header: 'Split date', key: 'split_date', width: 22 },
        { header: 'Customer', key: 'customer', width: 24 },
        { header: 'Distributor', key: 'distributor', width: 24 },
        { header: 'Product', key: 'product', width: 32 },
        { header: 'Pack Size', key: 'pack_size', width: 10 },
        { header: 'Quantity ordered', key: 'quantity_ordered', width: 16 },
        { header: 'Process', key: 'process', width: 14 },
        { header: 'Price', key: 'price', width: 12 },
        { header: 'Waybill No.', key: 'waybill_number', width: 18 },
      ];
      for (const row of data) {
        sheet.addRow({
          ...row,
          split_date: row.split_date ? row.split_date.slice(0, 19) : '',
          date_order_placed: row.date_order_placed ? row.date_order_placed.slice(0, 19) : '',
          split_id: row.split_id != null ? row.split_id : '',
        });
      }
      const buffer = await workBook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, 'SplitOrders.xlsx');
    } finally {
      this.splitOrdersDownloadLoading = false;
    }
  }

  generateAndDownloadDelivery() {

    // if (this.diffDateRangeForm.invalid) return;

    // const payload: StartAndEndDatePayload = {
    //   start_date: this._utilService.bestBeforeTime(
    //     this.diffDateRangeForm.value.start.toISOString()
    //   ),
    //   end_date: this._utilService.bestBeforeTime(
    //     this.diffDateRangeForm.value.end.toISOString()
    //   ),
    // };
    if (!(this.selectedDeliveryStartDate && this.selectedDeliveryEndDate && this.selectedCriteria)) {
      return;
    }
  
    const startValue: string =
      this.selectedDeliveryStartDate.getFullYear() +
      '-' +
      (this.selectedDeliveryStartDate.getMonth() + 1) +
      '-' +
      this.selectedDeliveryStartDate.getDate();
  
    const endValue: string =
      this.selectedDeliveryEndDate.getFullYear() +
      '-' +
      (this.selectedDeliveryEndDate.getMonth() + 1) +
      '-' +
      this.selectedDeliveryEndDate.getDate();
  
    const payload: DeliveryPayload = {
      start_date: startValue,
      end_date: endValue,
      type: this.selectedCriteria,
      company_id: this.selectedDistributor ? this.selectedDistributor.id : null,
    };
    console.log(`Del Payload : ${JSON.stringify(payload)}`)

    if (this.downloadDelReportControl.value === 'csv')
      
      this.downloadDelCSV(payload);


    if (this.downloadDelReportControl.value === 'excel')
      this.downloadDelExcel(payload);
  }

  async downloadDelCSV(payload: StartAndEndDatePayload) {
    
    const data: any[] = await this._prescriptionService.reportOrder(payload);

    const rows: string[] = [
      `Order No.${this.seperator}Customer Order No.${this.seperator}Date Order Placed${this.seperator}Customer${this.seperator}Distributor${this.seperator}Product${this.seperator}Pack Size${this.seperator}Quantity ordered${this.seperator}Original Quantity sent${this.seperator}Quantity on backorder${this.seperator}Process${this.seperator}Comment${this.seperator}INVOICE NR${this.seperator}INVOICE DATE${this.seperator}Waybill No.${this.seperator}Date Shipped${this.seperator}Date Collected Original${this.seperator}Date Collected Backorder${this.seperator}Script No${this.seperator}Comment Date BBP${this.seperator}Comments MBP${this.seperator}ORDER NR.${this.seperator}PRICE${this.seperator}Last Status Update`,
    ];

    if (data.length === 0) return;

    for (const order of data) {
      const attributes: string[] = [
        order.order_number + '',
        order.customer_order_number || '',
        order.date_order_placed || '',
        order.costomer || '',
        order.distributor || '',
        order.product || '',
        order.pack_size + '',
        order.quantity_ordered + '',
        '',
        '',
        order.process || '',
        '',
        '',
        '',
        order.waybill_number || '',
        order.date_shipped + '',
        '',
        '',
        '',
        '',
        '',
        '',
        order.price + '',
        order.last_update || '',
      ];

      const row: string = attributes.join(this.seperator);
      rows.push(row);
    }

    const csvArray: string = rows.join('\r\n');
    const blob: Blob = new Blob([csvArray], { type: 'text/csv' });
    saveAs(blob, 'Deliveries.csv');
  }

  async downloadDelExcel(payload: StartAndEndDatePayload) {
    const workBook: Workbook = new Workbook();
    workBook.creator = 'John Doe';
    workBook.lastModifiedBy = 'Soul Jah Love';
    workBook.created = new Date();
    // Force workbook calculation on load
    workBook.calcProperties.fullCalcOnLoad = true;

    workBook.views = [
      {
        x: 0,
        y: 0,
        width: 10000,
        height: 20000,
        firstSheet: 0,
        activeTab: 1,
        visibility: 'visible',
      },
    ];

    // Add orders sheet
    const sheet: Worksheet = workBook.addWorksheet('Orders');

    sheet.columns = [
      { header: 'Order No.', key: 'order_number', width: 32 },
      { header: 'Customer Order No.', key: 'customer_order_number', width: 32 },
      { header: 'Date Order Placed', key: 'date_order_placed', width: 32 },
      { header: 'Customer', key: 'customer', width: 32 },
      { header: 'Distributor', key: 'distributor', width: 32 },
      { header: 'Product', key: 'product', width: 32 },
      { header: 'Pack Size', key: 'pack_size', width: 32 },
      { header: 'Quantity ordered', key: 'quantity_ordered', width: 32 },
      { header: 'Total Price', key: 'total_price', width: 32 },
      { header: 'Original Quantity sent', key: 'quantity_sent', width: 32 },
      { header: 'Quantity on backorder', key: 'quantity_backorder', width: 32 },
      { header: 'Process', key: 'process', width: 32 },
      { header: 'Comment', key: 'comment', width: 32 },
      { header: 'Waybill No.', key: 'waybill_number', width: 32 },
      { header: 'Date Shipped', key: 'shipped_date', width: 32 },
      { header: 'Last Status Update', key: 'last_status_update', width: 32 },
      { header: 'Split Order No.', key: 'split_order_number', width: 32 },

    ];

    const orders: any[] = await this._prescriptionService.ShippingOrders(
      payload
    );

    if (orders.length === 0) return;

    // Set currency format for the Total Price column
    const totalPriceColumn = sheet.getColumn('total_price');
    totalPriceColumn.numFmt = 'R#,##0.00';

    for (const order of orders) {
       // Calculate total price by multiplying quantity_ordered by price
       const quantity = order.quantity_ordered || 0;
       const price = order.price || 0;
       const totalPrice = quantity * price;
	       
       // Store as number for proper Excel formatting
       order.total_price = totalPrice;

      order.shipped_date = order.shipped_date ? order.shipped_date.slice(0, 10) : ''; // 'YYYY-MM-DD' in UTC
      sheet.addRow(order);
    }

    const buffer = await workBook.xlsx.writeBuffer();

    const blob: Blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, 'Deliveries.xlsx');
  }

  handleMedicineFormChanges() {
    this.medicineDateRangeForm.controls['start'].valueChanges.subscribe(
      (value) => {
        this.selectedMedicineStartDate = value;
        this.selectedMedicineEndDate = null;
        this.reloadMedicineReportData();
      }
    );

    this.medicineDateRangeForm.controls['end'].valueChanges.subscribe(
      (value) => {
        this.selectedMedicineEndDate = value;
        this.reloadMedicineReportData();
      }
    );

    this.medicineDateRangeForm.controls['criteria'].valueChanges.subscribe(
      (value) => {
        this.selectedCriteria = value;
        this.reloadMedicineReportData();
      }
    );

    this.medicineDateRangeForm.controls['doctorName'].valueChanges.subscribe(
      (value) => {
        (async () => {
          this.doctors = await this._individualService.getIndividuals(
            1,
            10,
            '',
            value
          );
        })();
      }
    );

    this.medicineDateRangeForm.controls['companyName'].valueChanges.subscribe(
      (value) => {
        (async () => {
          this.companies = await this._companyService.getCompanies(
            1,
            10,
            value
          );
        })();
      }
    );
  }

  async reloadMedicineReportData() {
    if (
      !(
        this.selectedMedicineStartDate &&
        this.selectedMedicineEndDate &&
        this.selectedCriteria
      )
    )
      return;
    const startValue: string =
      this.selectedMedicineStartDate.getFullYear() +
      '-' +
      (this.selectedMedicineStartDate.getMonth() + 1) +
      '-' +
      this.selectedMedicineStartDate.getDate();

    const endValue: string =
      this.selectedMedicineEndDate.getFullYear() +
      '-' +
      (this.selectedMedicineEndDate.getMonth() + 1) +
      '-' +
      this.selectedMedicineEndDate.getDate();

    const payload: DashboardPayload = {
      start_date: startValue,
      end_date: endValue,
      type: this.selectedCriteria,
      doctor_id: this.selectedDoctor ? this.selectedDoctor.id : undefined,
      company_id: this.selectedCompany ? this.selectedCompany.id : undefined,
    };

    this.medicineReportData = await this._dashboardService.getMedicineData(
      payload
    );

    // find the appropirate criteria name
    const criteria: Criteria | undefined = this.criterias.find(
      (criteria) => criteria.value === this.medicineDateRangeForm.value.criteria
    );

    this.medicineCriteriaName = criteria
      ? criteria.type.slice(0, criteria.value.length - 2)
      : '-';

    this.medicineCriteriaName =
      this.medicineCriteriaName === 'Dai' ? 'Day' : this.medicineCriteriaName;

    this.medicineReportConfig = {
      ...this.medicineReportConfig,
      xAxisLabel: this.medicineCriteriaName,
    };
  }

  updateView(dashboardData: DashboardData) {
    this.medicineReportData = dashboardData.medicine;
    this.batchesData = dashboardData.batches;
    this.ordersData = dashboardData.orders;
  }

  generateAndDownloadOrders() {
    if (this.ordersDateRangeForm.invalid) return;

    const payload: StartAndEndDatePayload = {
      start_date: this._utilService.bestBeforeTime(
        this.ordersDateRangeForm.value.start.toISOString()
      ),
      end_date: this._utilService.bestBeforeTime(
        this.ordersDateRangeForm.value.end.toISOString()
      ),
    };

    if (this.downloadOrdersReportControl.value === 'csv')
      this.downloadOrdersCSV(payload);

    if (this.downloadOrdersReportControl.value === 'excel')
      this.downloadOrdersExcel(payload);
  }

  async downloadOrdersExcel(payload: StartAndEndDatePayload) {
    const workBook: Workbook = new Workbook();
    workBook.creator = 'John Doe';
    workBook.lastModifiedBy = 'Soul Jah Love';
    workBook.created = new Date();
    // Force workbook calculation on load
    workBook.calcProperties.fullCalcOnLoad = true;

    workBook.views = [
      {
        x: 0,
        y: 0,
        width: 10000,
        height: 20000,
        firstSheet: 0,
        activeTab: 1,
        visibility: 'visible',
      },
    ];

    // Add orders sheet
    const sheet: Worksheet = workBook.addWorksheet('Orders');

    sheet.columns = [
      { header: 'Order No.', key: 'order_number', width: 32 },
      { header: 'Customer Order No.', key: 'customer_order_number', width: 32 },
      { header: 'Date Order Placed', key: 'date_order_placed', width: 32 },
      { header: 'Customer', key: 'customer', width: 32 },
      { header: 'Distributor', key: 'distributor', width: 32 },
      { header: 'Product', key: 'product', width: 32 },
      { header: 'Pack Size', key: 'pack_size', width: 32 },
      { header: 'Quantity ordered', key: 'quantity_ordered', width: 32 },
      {
        header: 'Original Quantity sent',
        key: 'original_quantity_sent',
        width: 32,
      },
      {
        header: 'Quantity on backorder',
        key: 'quantity_on_backorder',
        width: 32,
      },
      { header: 'Process', key: 'process', width: 32 },
      { header: 'Comment', key: 'comment', width: 32 },
      { header: 'INVOICE NR', key: 'invoice_nr', width: 32 },
      { header: 'INVOICE DATE', key: 'invoice_date', width: 32 },
      { header: 'Waybill No.', key: 'waybill_number', width: 32 },
      { header: 'Date Shipped', key: 'date_shipped', width: 32 },
      {
        header: 'Date Collected Original',
        key: 'date_collected_original',
        width: 32,
      },
      {
        header: 'Date Collected Backorder',
        key: 'date_collected_backorder',
        width: 32,
      },
      { header: 'Script No', key: 'script_no', width: 32 },
      { header: 'Comment Date BBP', key: 'comment_date_bbp', width: 32 },
      { header: 'Comments MBP', key: 'comment_mbp', width: 32 },
      { header: 'ORDER NR.', key: 'order_nr', width: 32 },
      { header: 'PRICE', key: 'price', width: 32 },
      { header: 'Last Status Update', key: 'last_update', width: 32 },
    ];

    const orders: Order[] = await this._prescriptionService.reportOrder(
      payload
    );

    if (orders.length === 0) return;

    for (const order of orders) {
      sheet.addRow(order);
    }

    const buffer = await workBook.xlsx.writeBuffer();

    const blob: Blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, 'Orders.xlsx');
  }

  async downloadOrdersCSV(payload: StartAndEndDatePayload) {
    // get orders
    const rows: string[] = [
      `Order No.${this.seperator}Customer Order No.${this.seperator}Date Order Placed${this.seperator}Customer${this.seperator}Distributor${this.seperator}Product${this.seperator}Pack Size${this.seperator}Quantity ordered${this.seperator}Original Quantity sent${this.seperator}Quantity on backorder${this.seperator}Process${this.seperator}Comment${this.seperator}INVOICE NR${this.seperator}INVOICE DATE${this.seperator}Waybill No.${this.seperator}Date Shipped${this.seperator}Date Collected Original${this.seperator}Date Collected Backorder${this.seperator}Script No${this.seperator}Comment Date BBP${this.seperator}Comments MBP${this.seperator}ORDER NR.${this.seperator}PRICE${this.seperator}Last Status Update`,
    ];
    const orders: any[] = await this._prescriptionService.reportOrder(payload);
    if (orders.length === 0) return;

    for (const order of orders) {
      const attributes: string[] = [
        order.order_number + '',
        order.customer_order_number || '',
        order.date_order_placed || '',
        order.costomer || '',
        order.distributor || '',
        order.product || '',
        order.pack_size + '',
        order.quantity_ordered + '',
        '',
        '',
        order.process || '',
        '',
        '',
        '',
        order.waybill_number || '',
        order.date_shipped + '',
        '',
        '',
        '',
        '',
        '',
        '',
        order.price + '',
        order.last_update || '',
      ];

      const row: string = attributes.join(this.seperator);
      rows.push(row);
    }
    const csvArray: string = rows.join('\r\n');
    const blob: Blob = new Blob([csvArray], { type: 'text/csv' });
    saveAs(blob, 'Orders.csv');
  }

  async generateAndDownloadBatches() {
    if (this.batchesDateRangeForm.invalid) return;

    const payload: StartAndEndDatePayload = {
      start_date: this._utilService.bestBeforeTime(
        this.batchesDateRangeForm.value.start.toISOString()
      ),
      end_date: this._utilService.bestBeforeTime(
        this.batchesDateRangeForm.value.end.toISOString()
      ),
    };

    if (this.downloadBatchesReportControl.value === 'csv')
      this.downloadBatchesCSV(payload);

    if (this.downloadBatchesReportControl.value === 'excel')
      this.downloadBatchesExcel(payload);
  }

  async downloadBatchesCSV(payload: StartAndEndDatePayload) {
    const rows: string[] = [
      `Batch number${this.seperator}Medicine${this.seperator}Quantity produced${this.seperator}Remaining quantity${this.seperator}Status${this.seperator}Created,Updated`,
    ];

    const batches: Batch[] = await this._batchService.reportBatch(payload);

    if (batches.length === 0) return;

    for (const batch of batches) {
      // create individual row
      const attributes: string[] = [
        batch.batch_number,
        batch.medicine_dosage_type.medicine.name,
        batch.quantity_produced.toString(),
        batch.stock_remaining ? batch.stock_remaining.toString() : '',
        batch.status ? batch.status.status : '-',
        batch.created_at,
        batch.updated_at ? batch.updated_at : '-',
      ];

      const row: string = attributes.join(this.seperator);
      rows.push(row);
    }

    const csvArray: string = rows.join('\r\n');
    const blob: Blob = new Blob([csvArray], { type: 'text/csv' });
    saveAs(blob, 'Batches.csv');
  }

  async downloadBatchesExcel(payload: StartAndEndDatePayload) {
    const workBook: Workbook = new Workbook();
    workBook.creator = 'John Doe';
    workBook.lastModifiedBy = 'Soul Jah Love';
    workBook.created = new Date();
    // Force workbook calculation on load
    workBook.calcProperties.fullCalcOnLoad = true;

    workBook.views = [
      {
        x: 0,
        y: 0,
        width: 10000,
        height: 20000,
        firstSheet: 0,
        activeTab: 1,
        visibility: 'visible',
      },
    ];

    // Add orders sheet
    const sheet: Worksheet = workBook.addWorksheet('Batches');

    sheet.columns = [
      { header: 'Batch number', key: 'batch_number', width: 32 },
      { header: 'Medicine', key: 'medicine', width: 32 },
      { header: 'Quantity produced', key: 'quantity_produced', width: 32 },
      { header: 'Remaining quantity', key: 'remaining_quantity', width: 32 },
      { header: 'Status', key: 'status', width: 32 },
      { header: 'Date created', key: 'created_at', width: 32 },
      { header: 'Date updated', key: 'updated_at', width: 32 },
    ];

    const batches: Batch[] = await this._batchService.reportBatch(payload);

    if (batches.length === 0) return;

    for (const batch of batches) {
      // create individual row
      sheet.addRow({
        batch_number: batch.batch_number,
        medicine: batch.medicine_dosage_type.medicine.name,
        quantity_produced: batch.quantity_produced,
        remaining_quantity: batch.stock_remaining ? batch.stock_remaining : '',
        status: batch.status ? batch.status.status : '-',
        created_at: batch.created_at,
        updated_at: batch.updated_at,
      });
    }

    const buffer = await workBook.xlsx.writeBuffer();

    const blob: Blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, 'Batches.xlsx');
  }

  async generateAndDownloadMedicine() {
    if (this.downloadMedicineReportControl.value === 'csv')
      this.downloadMedicineCSV();
    if (this.downloadMedicineReportControl.value === 'excel')
      this.downloadMedicineExcel();
  }

  async downloadMedicineExcel() {
    const workBook: Workbook = new Workbook();
    workBook.creator = 'John Doe';
    workBook.lastModifiedBy = 'Soul Jah Love';
    workBook.created = new Date();
    // Force workbook calculation on load
    workBook.calcProperties.fullCalcOnLoad = true;

    workBook.views = [
      {
        x: 0,
        y: 0,
        width: 10000,
        height: 20000,
        firstSheet: 0,
        activeTab: 1,
        visibility: 'visible',
      },
    ];

    // Add orders sheet
    const sheet: Worksheet = workBook.addWorksheet('Medicine');

    // Get medicine data
    if (
      !(
        this.selectedMedicineStartDate &&
        this.selectedMedicineEndDate &&
        this.selectedCriteria
      )
    )
      return;
    const startValue: string =
      this.selectedMedicineStartDate.getFullYear() +
      '-' +
      (this.selectedMedicineStartDate.getMonth() + 1) +
      '-' +
      this.selectedMedicineStartDate.getDate();

    const endValue: string =
      this.selectedMedicineEndDate.getFullYear() +
      '-' +
      (this.selectedMedicineEndDate.getMonth() + 1) +
      '-' +
      this.selectedMedicineEndDate.getDate();

    const payload: MedicineReportPayload = {
      start_date: startValue,
      end_date: endValue,
      type: this.selectedCriteria,
      doctor_id: this.selectedDoctor ? this.selectedDoctor.id : undefined,
      company_id: this.selectedCompany ? this.selectedCompany.id : undefined,
      format: 'xls',
    };

    const data: any = await this._dashboardService.getMedicineDataInExcelFormat(
      payload
    );
    switch (this.selectedCriteria) {
      case 'monthly':
        {
          sheet.columns = [
            { header: 'Month', key: 'month', width: 32 },
            { header: 'Name + Pack size', key: 'name', width: 32 },
            { header: 'Total Quantity', key: 'total_quantity', width: 32 },
            { header: 'Stock on Hand', key: 'stock_on_hand', width: 32 },
          ];

          // Iterate through the data
          for (const name of Object.keys(data)) {
            const medicine: any = data[name];

            for (const month of Object.keys(medicine)) {
              if (month !== 'contained_quantity') {
                // insert a row
                sheet.addRow({
                  month: month,
                  name: name,
                  total_quantity:
                    medicine[month] !== 0 ? medicine[month].total_quantity : 0,
                  stock_on_hand:
                    medicine[month] !== 0 ? medicine[month].stock_on_hand : 0,
                });
              }
            }
          }
          const buffer = await workBook.xlsx.writeBuffer();

          const blob: Blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          saveAs(blob, 'Medicine.xlsx');
        }
        break;

      case 'weekly':
        {
          sheet.columns = [
            { header: 'Week', key: 'week', width: 32 },
            { header: 'Name + Pack size', key: 'name', width: 32 },
            { header: 'Quantity', key: 'quantity', width: 32 },
          ];

          // Iterate through the data
          for (const name of Object.keys(data)) {
            const medicine: any = data[name];

            for (const week of Object.keys(medicine)) {
              if (week !== 'contained_quantity') {
                // insert a row
                sheet.addRow({
                  week: week,
                  name: name,
                  quantity:
                    medicine[week] !== 0 ? medicine[week].total_quantity : 0,
                });
              }
            }
          }

          const buffer = await workBook.xlsx.writeBuffer();

          const blob: Blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          saveAs(blob, 'Medicine.xlsx');
        }
        break;

      case 'daily':
        {
          sheet.columns = [
            { header: 'Day', key: 'day', width: 32 },
            { header: 'Name + Pack size', key: 'name', width: 32 },
            { header: 'Quantity', key: 'quantity', width: 32 },
          ];

          // Iterate through the columns
          for (const name of Object.keys(data)) {
            const medicine: any = data[name];

            for (const day of Object.keys(medicine)) {
              if (day !== 'contained_quantity') {
                // insert a row
                sheet.addRow({
                  day: day,
                  name: name,
                  quantity:
                    medicine[day] !== 0 ? medicine[day].total_quantity : 0,
                });
              }
            }
          }

          const buffer = await workBook.xlsx.writeBuffer();

          const blob: Blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          saveAs(blob, 'Medicine.xlsx');
        }
        break;
    }
  }

  async downloadMedicineCSV() {
    if (
      !(
        this.selectedMedicineStartDate &&
        this.selectedMedicineEndDate &&
        this.selectedCriteria
      )
    )
      return;
    const startValue: string =
      this.selectedMedicineStartDate.getFullYear() +
      '-' +
      (this.selectedMedicineStartDate.getMonth() + 1) +
      '-' +
      this.selectedMedicineStartDate.getDate();

    const endValue: string =
      this.selectedMedicineEndDate.getFullYear() +
      '-' +
      (this.selectedMedicineEndDate.getMonth() + 1) +
      '-' +
      this.selectedMedicineEndDate.getDate();

    const payload: MedicineReportPayload = {
      start_date: startValue,
      end_date: endValue,
      type: this.selectedCriteria,
      doctor_id: this.selectedDoctor ? this.selectedDoctor.id : undefined,
      company_id: this.selectedCompany ? this.selectedCompany.id : undefined,
      format: 'xls',
    };

    const data: any = await this._dashboardService.getMedicineDataInExcelFormat(
      payload
    );

    switch (this.selectedCriteria) {
      case 'monthly':
        {
          const rows: string[] = [
            `Month${this.seperator}Name + Pack size'${this.seperator}Total quantity`,
          ];

          // Iterate through the data
          for (const name of Object.keys(data)) {
            const medicine: any = data[name];

            for (const month of Object.keys(medicine)) {
              if (month !== 'contained_quantity') {
                // insert a row

                const attributes: string[] = [
                  month,
                  name,
                  medicine[month] !== 0 ? medicine[month].total_quantity : 0,
                ];
                const row: string = attributes.join(this.seperator);
                rows.push(row);
              }
            }
          }

          const csvArray: string = rows.join('\r\n');
          const blob: Blob = new Blob([csvArray], { type: 'text/csv' });
          saveAs(blob, 'Medicine.csv');
        }
        break;

      case 'weekly':
        {
          const rows: string[] = [
            `Week${this.seperator}Name + Pack size'${this.seperator}Total quantity`,
          ];

          // Iterate through the data
          for (const name of Object.keys(data)) {
            const medicine: any = data[name];

            for (const week of Object.keys(medicine)) {
              if (week !== 'contained_quantity') {
                // insert a row

                const attributes: string[] = [
                  week,
                  name,
                  medicine[week] !== 0 ? medicine[week].total_quantity : 0,
                ];
                const row: string = attributes.join(this.seperator);
                rows.push(row);
              }
            }
          }

          const csvArray: string = rows.join('\r\n');
          const blob: Blob = new Blob([csvArray], { type: 'text/csv' });
          saveAs(blob, 'Medicine.csv');
        }
        break;

      case 'daily':
        {
          const rows: string[] = [
            `Day${this.seperator}Name + Pack size'${this.seperator}Total quantity`,
          ];

          // Iterate through the data
          for (const name of Object.keys(data)) {
            const medicine: any = data[name];

            for (const day of Object.keys(medicine)) {
              if (day !== 'contained_quantity') {
                // insert a row

                const attributes: string[] = [
                  day,
                  name,
                  medicine[day] !== 0 ? medicine[day].total_quantity : 0,
                ];
                const row: string = attributes.join(this.seperator);
                rows.push(row);
              }
            }
          }

          const csvArray: string = rows.join('\r\n');
          const blob: Blob = new Blob([csvArray], { type: 'text/csv' });
          saveAs(blob, 'Medicine.csv');
        }
        break;
    }
  }

  doctorSelected(event: MatAutocompleteSelectedEvent) {
    this.selectedDoctor = event.option.value;

    if (!this.selectedDoctor) return;
    this.reloadMedicineReportData();
    this.patchControlEvenIfDisabled(
      this.medicineDateRangeForm,
      'doctorName',
      `${this.selectedDoctor.first_name} ${this.selectedDoctor.last_name}  `
    );
  }

  deselectDoctor() {
    this.selectedDoctor = null;
    this.patchControlEvenIfDisabled(this.medicineDateRangeForm, 'doctorName', '');
    this.reloadMedicineReportData();
  }

  companySelected(event: MatAutocompleteSelectedEvent) {
    this.selectedCompany = event.option.value;
    if (!this.selectedCompany) return;
    this.reloadMedicineReportData();
    this.patchControlEvenIfDisabled(
      this.medicineDateRangeForm,
      'companyName',
      this.selectedCompany.name
    );
  }

  deselectCompany() {
    this.selectedCompany = null;
    this.patchControlEvenIfDisabled(this.medicineDateRangeForm, 'companyName', '');
    this.reloadMedicineReportData();
  }

// Add new method to handle delivery form changes
handleDeliveryFormChanges() {
  this.deliveryReportDataForm.controls['start'].valueChanges.subscribe((value) => {
    this.selectedDeliveryStartDate = value;
    this.reloadDeliveryReportData();
  });

  this.deliveryReportDataForm.controls['end'].valueChanges.subscribe((value) => {
    this.selectedDeliveryEndDate = value;
    this.reloadDeliveryReportData();
  });

  this.deliveryReportDataForm.controls['criteria'].valueChanges.subscribe((value) => {
    this.selectedCriteria = value;
    this.reloadDeliveryReportData();
  });

  this.deliveryReportDataForm.controls['distributorName'].valueChanges.subscribe((value) => {
    (async () => {
      this.distributors = await this._companyService.getCompanies(1, 10, value);
    })();
  });
}

// Update the reloadDeliveryReportData function
async reloadDeliveryReportData() {
  if (!(this.selectedDeliveryStartDate && this.selectedDeliveryEndDate && this.selectedCriteria)) {
    return;
  }

  const startValue: string =
    this.selectedDeliveryStartDate.getFullYear() +
    '-' +
    (this.selectedDeliveryStartDate.getMonth() + 1) +
    '-' +
    this.selectedDeliveryStartDate.getDate();

  const endValue: string =
    this.selectedDeliveryEndDate.getFullYear() +
    '-' +
    (this.selectedDeliveryEndDate.getMonth() + 1) +
    '-' +
    this.selectedDeliveryEndDate.getDate();

  const payload: DeliveryPayload = {
    start_date: startValue,
    end_date: endValue,
    type: this.selectedCriteria,
    company_id: this.selectedDistributor ? this.selectedDistributor.id : undefined,
  };

  // Assuming you have a method to get delivery data - you might need to create this in your DashboardService
  // this.trackingDetails = await this._dashboardService.getDeliveryData(payload);
}

// Update distributorSelected function
distributorSelected(event: MatAutocompleteSelectedEvent) {
  this.selectedDistributor = event.option.value;
  if (!this.selectedDistributor) return;
  
  this.reloadDeliveryReportData();
  this.patchControlEvenIfDisabled(
    this.deliveryReportDataForm,
    'distributorName',
    this.selectedDistributor.name
  );
}

// Update deselectDistributor function
deselectDistributor() {
  this.selectedDistributor = null;
  this.patchControlEvenIfDisabled(this.deliveryReportDataForm, 'distributorName', '');
  this.reloadDeliveryReportData();
}

  /**
   * Client-side validation for Vetscripts report date: format YYYY-MM-DD, not future, within last 365 days.
   */
  private validateVetscriptsReportDate(dateStr: string): string | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return 'date must be YYYY-MM-DD';
    const d = new Date(dateStr + 'T12:00:00Z');
    if (isNaN(d.getTime())) return 'invalid date';
    const today = new Date();
    today.setUTCHours(23, 59, 59, 999);
    if (d > today) return 'date cannot be in the future';
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() - 365);
    minDate.setUTCHours(0, 0, 0, 0);
    if (d < minDate) return 'date must be within the last 365 days';
    return null;
  }

  /** Generate and download Vetscripts daily shipped report via signed URL. */
  async generateVetscriptsShippedReport() {
    const raw = this.vetscriptsReportDateControl.value;
    let dateParam: string | undefined;
    if (raw) {
      const y = raw.getFullYear();
      const m = String(raw.getMonth() + 1).padStart(2, '0');
      const d = String(raw.getDate()).padStart(2, '0');
      dateParam = `${y}-${m}-${d}`;
      const err = this.validateVetscriptsReportDate(dateParam);
      if (err) {
        this._utilService.showError({ error: { message: err } });
        return;
      }
    }
    this.vetscriptsReportLoading = true;
    try {
      const result = await this._prescriptionService.getVetscriptsShippedDailyReport(dateParam);
      if (result?.url) {
        window.open(result.url, '_blank');
        this._alertService.openSnackBar('Report ready. Link valid for 1 hour.');
      }
    } finally {
      this.vetscriptsReportLoading = false;
    }
  }
}
